import { MinusCircleIcon, PencilSquareIcon } from '@heroicons/react/16/solid';
import { isToday } from 'date-fns';
import { Fragment } from 'react';
import { twMerge } from 'tailwind-merge';

import { compactLineLabel } from '@/renderer/utils/formatMarketLine';
import { formatReleasePickParts } from '@/renderer/utils/formatReleasePick';
import { isExpectedLive } from '@/renderer/utils/liveWindow';
import { useLocale } from '@/renderer/utils/locale';

import UpdateReleaseForm from './UpdateReleaseForm';

// ── Contextual empty state ──────────────────────────────────────────────────────

/**
 * A soft empty state (icon circle + title + subtext + optional CTA), sized to sit
 * inside a list panel. Matches the #1996 Empty aesthetic but carries an optional CTA
 * so a fresh subscriber can jump to the full track record (#2004).
 */
export function ResultsEmpty({
  Icon,
  title,
  description,
  ctaLabel,
  ctaHref,
}: {
  Icon:
    | React.ForwardRefExoticComponent<React.PropsWithoutRef<React.SVGProps<SVGSVGElement>>>
    | ((props: React.ComponentPropsWithoutRef<'svg'>) => JSX.Element);
  title: React.ReactNode;
  description: React.ReactNode;
  ctaLabel?: string;
  ctaHref?: string;
}) {
  return (
    <div className="flex select-none flex-col items-center px-6 py-10 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800/60">
        <Icon className="size-6 stroke-[1.6] text-zinc-400 dark:text-zinc-500" />
      </span>
      <h4 className="mt-3.5 font-semibold text-[15px] text-zinc-900 dark:text-white">{title}</h4>
      <p className="mt-1 max-w-sm text-sm text-zinc-500 leading-6 dark:text-zinc-400">
        {description}
      </p>
      {ctaLabel && ctaHref && (
        <a
          href={ctaHref}
          className="mt-3 font-semibold text-emerald-600 text-sm hover:text-emerald-500 dark:text-emerald-400"
        >
          {ctaLabel}
        </a>
      )}
    </div>
  );
}

// ── Small formatters ────────────────────────────────────────────────────────────

/** "KC 27-14" (winner first) with an OT suffix; "" when there's no final score. */
export function formatScore(
  visitorTeam: string | undefined,
  homeTeam: string | undefined,
  visitorScore: number,
  homeScore: number,
  overtimes: number,
) {
  let score = '';
  if (visitorScore > homeScore) {
    score = `${visitorTeam} ${visitorScore}-${homeScore}`;
  } else if (homeScore > visitorScore) {
    score = `${homeTeam} ${homeScore}-${visitorScore}`;
  } else if (homeScore === visitorScore && homeScore !== null) {
    score = `TIE ${homeScore}-${visitorScore}`;
  }
  if (overtimes > 0) {
    score += `${score ? ' ' : ''}${overtimes > 1 ? overtimes : ''}OT`;
  }
  return score;
}

/** A signed value ("+3", "-1.5"); "0" → the given zero label; null → the nullish label. */
export function formatValue(
  value: number | null | undefined,
  nullish: string | JSX.Element = '',
  zero: string | JSX.Element | undefined = undefined,
) {
  if (value === null || value === undefined) {
    return nullish;
  }
  if (value > 0) {
    return `+${Number(value)}`;
  }
  if (value === 0 && zero !== undefined) {
    return zero;
  }
  return `${Number(value)}`;
}

/** Coerce a numeric field that the API may serialize as a string ("1.000"). */
function num(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The per-play units result as a display string. Free plays don't count → em-dash. */
function unitsLabel(unit: number | string | null | undefined, free?: boolean) {
  if (free) {
    return '—';
  }
  const n = num(unit);
  if (n === null) {
    return '';
  }
  if (n > 0) {
    return `+${n.toFixed(1)}u`;
  }
  if (n < 0) {
    return `${n.toFixed(1)}u`;
  }
  return '0.0u';
}

// ── Status pill ─────────────────────────────────────────────────────────────────

const RESULT_LABEL: Record<string, string> = {
  win: 'Win',
  loss: 'Loss',
  push: 'Push',
  canceled: 'Canceled',
  noAction: 'No Action',
};

const PILL_STYLE: Record<string, string> = {
  win: 'text-emerald-600 bg-emerald-500/12 dark:text-emerald-400 dark:bg-emerald-400/15',
  loss: 'text-rose-600 bg-rose-500/12 dark:text-rose-400 dark:bg-rose-500/15',
  push: 'text-zinc-500 bg-zinc-500/14 dark:text-zinc-300 dark:bg-white/10',
  canceled: 'text-zinc-500 bg-zinc-500/14 dark:text-zinc-300 dark:bg-white/10',
  noAction: 'text-zinc-500 bg-zinc-500/14 dark:text-zinc-300 dark:bg-white/10',
  live: 'text-amber-600 bg-amber-500/14 dark:text-amber-300 dark:bg-amber-400/15',
  upcoming: 'text-zinc-600 bg-zinc-100 dark:text-zinc-300 dark:bg-white/5',
};

const DOT_STYLE: Record<string, string> = {
  win: 'bg-emerald-500',
  loss: 'bg-rose-500',
  push: 'bg-zinc-400',
  canceled: 'bg-zinc-400',
  noAction: 'bg-zinc-400',
  live: 'bg-amber-500 animate-pulse',
};

function StatusPill({ kind, children }: { kind: string; children: React.ReactNode }) {
  return (
    <span
      className={twMerge(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 font-semibold text-xs',
        PILL_STYLE[kind] || PILL_STYLE.upcoming,
      )}
    >
      {DOT_STYLE[kind] && (
        <span className={twMerge('size-1.5 shrink-0 rounded-full', DOT_STYLE[kind])} />
      )}
      {children}
    </span>
  );
}

const FreePill = () => (
  <span className="shrink-0 whitespace-nowrap rounded-[5px] bg-violet-100 px-1.5 py-0.5 font-bold text-[9px] text-violet-600 uppercase tracking-wide dark:bg-violet-400/15 dark:text-violet-300">
    Free
  </span>
);

const GHOST_TITLE =
  'Released before your subscription, so it is not counted on your record. It disappears once it grades.';

/**
 * A "ghost" play: visible through the subscription but with no receipt (released before
 * it started), so it never counts on the record and drops off once it grades.
 *
 * Deliberately NOT a clock or hourglass. This was originally an amber tint + dashed
 * amber border + a clock, which sat beside the amber "Live" pill and read as *more*
 * pending — the opposite of the meaning. Any time metaphor says "waiting"; the point
 * here is "excluded", so it's a minus-circle. The Pending section repeats this same
 * icon in its footnote so the mark is decodable (#2004).
 */
export const GhostMark = ({ className }: { className?: string }) => (
  <MinusCircleIcon
    title={GHOST_TITLE}
    aria-label={GHOST_TITLE}
    className={twMerge('inline-block size-4 shrink-0 text-zinc-400 dark:text-zinc-500', className)}
  />
);

// ── One play row ────────────────────────────────────────────────────────────────

/**
 * A single results/pending play, rendered as a custom row (#2004): the pick leads as the
 * prominent title, the result pill + units (or, for a pending pick, the current market line)
 * anchor the right, and one muted meta line carries `sport · time/score · close · CLV`.
 * A pending row leads its title with the rotation number (what you call the bet in
 * with); the score shows once. Used for both the Pending and Results sections and for
 * both the member (`me`) and public (`archive`) views.
 */
export function PlayRow({
  release,
  isGhost = false,
  isAdmin = false,
  onGrade,
}: {
  release: API.Release;
  isGhost?: boolean;
  isAdmin?: boolean;
  onGrade?: () => void;
}) {
  const intl = useLocale();
  const graded = release.result !== 'pending';
  const started = !!release.gameTime && new Date(release.gameTime).getTime() <= Date.now();
  // "Live" is bounded, not just "kickoff has passed" — that was true forever, so an
  // ungraded play from a past season sat here claiming to be live. Past the window a
  // pending play is late to grade, which is a different (and honest) thing to say.
  // We have no live feed on this page: the ESPN-backed scores are admin-only, so this
  // is a schedule guess and deliberately says nothing about the score (#2004).
  const pendingKind = !graded && isExpectedLive(release) ? 'live' : started ? 'late' : 'upcoming';

  // Two widths: the short ESPN abbreviation on mobile, the fuller name (NFL nickname or
  // school name) on desktop — the abbreviation reads too terse on a wide row (#2004).
  const short = formatReleasePickParts(release, 'short');
  const long = formatReleasePickParts(release, 'long');

  const score = formatScore(
    release.visitor?.shortName || release.visitor?.name,
    release.home?.shortName || release.home?.name,
    release.visitorScore,
    release.homeScore,
    release.overtimes,
  );

  // The close/market number, team-less (the picked team already leads the title).
  const closeNumber =
    release.playType === 'side'
      ? release.closingSide
      : release.playType === 'total'
        ? release.closingTotal
        : null;
  const closeLabel = compactLineLabel(release, closeNumber);
  const marketLabel = release.currentLine
    ? compactLineLabel(release, release.currentLine.number)
    : '';
  const clv = num(release.closingLineValue);
  const unit = num(release.unit);

  // The rotation number is how you call a bet in at the book, so it earns its place on a
  // pending row and only there — once the play grades it is dead weight. Futures and
  // props carry 0/none. It leads the pick on the same line, the way a book lists it,
  // rather than sitting under it where it read as a second sport label (#2004).
  const rotation = `${release.rotation ?? ''}`.trim();
  const showRotation = !graded && rotation !== '' && rotation !== '0';

  // Pending games time; "Today 3:05 PM" or "Sat, Sep 13 · 11:00 AM".
  let gameTimeInline = '';
  if (release.gameTime) {
    const todayGame = isToday(new Date(release.gameTime));
    const timePart = intl.formatDate(release.gameTime, {
      weekday: undefined,
      month: undefined,
      day: undefined,
      year: undefined,
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: undefined,
    });
    if (todayGame) {
      gameTimeInline = `Today ${timePart}`;
    } else {
      const datePart = intl.formatDate(release.gameTime, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: undefined,
        hour: undefined,
        minute: undefined,
      });
      gameTimeInline = `${datePart} · ${timePart}`;
    }
  }

  return (
    <div
      className={twMerge(
        'flex items-center gap-3.5 border-zinc-950/[0.06] border-t px-4 py-3 transition first:border-t-0 hover:bg-zinc-50 dark:border-white/[0.07] dark:hover:bg-white/[0.02]',
      )}
    >
      {/* Left: pick title + muted meta line. */}
      <div className="min-w-0 flex-1">
        {/* Spacing lives on the container, not the badges: an `ml-*` on each badge
            reads as a stray indent once one wraps onto its own line (#2004). */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold text-[15px] text-zinc-900 leading-snug dark:text-white">
          {showRotation && (
            // Smaller and lighter than the pick: the number leads the line the way a book
            // lists it, but it is a lookup key, not the thing you are reading for.
            <span className="shrink-0 font-medium text-[13px] text-zinc-400 tabular-nums dark:text-zinc-500">
              {rotation}
            </span>
          )}
          <span className="break-words">
            <span className="sm:hidden">{short.pick}</span>
            <span className="hidden sm:inline">{long.pick}</span>
            {/* The matchup is context, not the bet — keeping it at the pick's weight
                left three things on the line all shouting equally (#2004). */}
            {!!long.opponent && (
              <span className="font-normal text-zinc-400 dark:text-zinc-500">
                {' '}
                <span className="sm:hidden">{short.opponent}</span>
                <span className="hidden sm:inline">{long.opponent}</span>
              </span>
            )}
          </span>
          {release.gameType === 'fcs' && (
            <span className="shrink-0 whitespace-nowrap rounded-[5px] bg-blue-400/20 px-1.5 py-0.5 font-medium text-blue-800 text-xs dark:bg-blue-400/25 dark:text-blue-300">
              FCS
            </span>
          )}
          {release.gameType === 'extra' && (
            <span className="shrink-0 whitespace-nowrap rounded-[5px] bg-purple-400/20 px-1.5 py-0.5 font-medium text-purple-800 text-xs dark:bg-purple-400/25 dark:text-purple-300">
              Extra
            </span>
          )}
          {!!release.free && <FreePill />}
          {isGhost && <GhostMark />}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-zinc-400 tabular-nums dark:text-zinc-500">
          {release.sport?.shortName && <span>{release.sport.shortName}</span>}
          {graded && score && (
            <>
              <span aria-hidden>·</span>
              <span className="font-medium text-zinc-500 dark:text-zinc-400">{score}</span>
            </>
          )}
          {/* Every pending row carries its game time. Suppressing it on a started game
              left that row with nothing at all in the meta line (#2004). */}
          {!graded && gameTimeInline && (
            <>
              <span aria-hidden>·</span>
              <span className="font-medium text-zinc-500 dark:text-zinc-400">{gameTimeInline}</span>
            </>
          )}
          {graded && closeLabel && (
            <>
              <span aria-hidden>·</span>
              <span>close {closeLabel}</span>
            </>
          )}
          {graded && clv != null && (
            <>
              <span aria-hidden>·</span>
              <span>
                CLV{' '}
                <span
                  className={twMerge(
                    'font-semibold',
                    clv > 0 && 'text-emerald-600 dark:text-emerald-400',
                    clv < 0 && 'text-rose-600 dark:text-rose-400',
                  )}
                >
                  {formatValue(clv, '', '+0.0')}
                </span>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Right: result pill + units (graded) or status + current market line (pending). */}
      <div className="flex shrink-0 flex-col items-end gap-1 text-right">
        {graded ? (
          <>
            <StatusPill kind={release.result}>{RESULT_LABEL[release.result]}</StatusPill>
            {(release.free || unit != null) && (
              // Muted + regular weight: the win/loss color lives in the pill above, so a
              // colored/bold units figure here would just compete and repeat it (#2004).
              <span className="font-medium text-[13px] text-zinc-500 tabular-nums dark:text-zinc-400">
                {unitsLabel(unit, release.free)}
              </span>
            )}
          </>
        ) : (
          <>
            {pendingKind === 'live' && <StatusPill kind="live">Live</StatusPill>}
            {pendingKind === 'late' && <StatusPill kind="upcoming">Awaiting result</StatusPill>}
            {pendingKind === 'upcoming' && <StatusPill kind="upcoming">Upcoming</StatusPill>}
            {marketLabel && (
              <span className="font-bold text-[13px] text-emerald-600 tabular-nums dark:text-emerald-400">
                {/* "current", not "live" (#2078): this is the market line still moving
                    before kickoff, and most rows carrying it have not started, so "live"
                    read as the game being underway. It leads the number the way
                    `close ...` leads a graded row's, so the two pair. "live" stays
                    reserved for a genuinely in-play state — nothing needs it today,
                    since the in-play market feed is dormant (#2023). */}
                <span className="mr-0.5 align-middle font-extrabold text-[8px] text-amber-500 uppercase tracking-wide dark:text-amber-400">
                  current
                </span>
                {marketLabel}
              </span>
            )}
          </>
        )}
      </div>

      {isAdmin && (
        <div className="flex shrink-0 items-center">
          <UpdateReleaseForm release={release} onSuccessfulSubmit={onGrade}>
            <PencilSquareIcon className="size-4 shrink-0 text-emerald-500 hover:text-emerald-400" />
          </UpdateReleaseForm>
        </div>
      )}
    </div>
  );
}

// ── Section header ──────────────────────────────────────────────────────────────

/** The uppercase section label ("Pending" / "Results") + an optional count chip. */
export function SectionHead({
  label,
  count,
  className,
}: {
  label: string;
  count?: number;
  className?: string;
}) {
  return (
    <div className={twMerge('mb-2 flex items-center gap-2.5 px-0.5', className)}>
      <h3 className="font-semibold text-xs text-zinc-500 uppercase tracking-wider dark:text-zinc-400">
        {label}
      </h3>
      {count != null && count > 0 && (
        <span className="rounded-full bg-zinc-100 px-2 py-0.5 font-semibold text-[11px] text-zinc-500 dark:bg-white/10 dark:text-zinc-300">
          {count}
        </span>
      )}
    </div>
  );
}

// ── The list panel ──────────────────────────────────────────────────────────────

function RowSkeleton({ isAdmin }: { isAdmin: boolean }) {
  return (
    <div className="flex items-center gap-3.5 border-zinc-950/[0.06] border-t px-4 py-3 first:border-t-0 dark:border-white/[0.07]">
      <div className="min-w-0 flex-1">
        <div className="h-4 w-2/3 animate-pulse rounded-sm bg-zinc-200 dark:bg-zinc-700" />
        <div className="mt-2 h-3 w-1/3 animate-pulse rounded-sm bg-zinc-200 dark:bg-zinc-700" />
      </div>
      <div className="h-5 w-16 shrink-0 animate-pulse rounded-md bg-zinc-200 dark:bg-zinc-700" />
      {isAdmin && (
        <div className="size-4 shrink-0 animate-pulse rounded-sm bg-zinc-200 dark:bg-zinc-700" />
      )}
    </div>
  );
}

/**
 * The bordered list panel that holds the play rows, with sticky-feeling date-group
 * headers, loading skeletons, and a caller-supplied empty state. `rows` are already
 * ordered by the request; grouping just breaks on a change of formatted date.
 */
export function PlayList({
  rows,
  isAdmin = false,
  isPending = false,
  onGrade,
  emptyState,
  dateBy = 'gameTime',
  groupByDate = true,
}: {
  // Either feed's rows — the shape is detected per row, not inferred from the viewer.
  rows: (API.Release | API.MessageReceipt)[];
  isAdmin?: boolean;
  isPending?: boolean;
  onGrade?: () => void;
  emptyState?: React.ReactNode;
  // Which timestamp drives the date group headers.
  dateBy?: 'gameTime' | 'createdAt';
  // The Results section groups rows under date headers; the Pending section is a flat
  // list (upcoming/live, no per-day headers).
  groupByDate?: boolean;
}) {
  const intl = useLocale();
  const hasRows = rows.length > 0;
  let lastDate: string | undefined;

  if (!hasRows && isPending) {
    return (
      <div className="overflow-hidden rounded-xl border border-zinc-950/[0.08] bg-white shadow-xs dark:border-white/10 dark:bg-zinc-900">
        {Array(4)
          .fill(0)
          .map((_, i) => (
            <RowSkeleton key={i} isAdmin={isAdmin} />
          ))}
      </div>
    );
  }

  if (!hasRows) {
    return (
      <div className="overflow-hidden rounded-xl border border-zinc-950/[0.08] bg-white shadow-xs dark:border-white/10 dark:bg-zinc-900">
        {emptyState ?? (
          <div className="py-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
            No results
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-950/[0.08] bg-white shadow-xs dark:border-white/10 dark:bg-zinc-900">
      {rows.map((row) => {
        // Detect the row's shape rather than inferring it from `userId`: the My/All
        // toggle flips userId while the previous feed's rows are still mounted, so a
        // userId-driven cast reads an archive Release as a receipt and crashes on the
        // missing `.release` (#2004). A receipt nests its release; a release is itself.
        const receipt = row as API.MessageReceipt;
        const release = (receipt.release ?? (row as API.Release)) as API.Release | undefined;
        if (!release) {
          return null;
        }
        const isGhost = !!receipt.ghost;

        const stamp =
          dateBy === 'gameTime' ? release.gameTime || release.createdAt : release.createdAt;
        const nextDate = intl.formatDate(stamp, {
          timeZone: 'America/Chicago',
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        const showDate = groupByDate && lastDate !== nextDate;
        lastDate = nextDate;

        return (
          <Fragment key={release.id}>
            {showDate && (
              <div className="border-zinc-950/[0.06] border-t bg-zinc-50 px-4 py-1.5 font-semibold text-xs text-zinc-500 first:border-t-0 dark:border-white/[0.07] dark:bg-white/[0.03] dark:text-zinc-400">
                {nextDate}
              </div>
            )}
            <PlayRow release={release} isGhost={isGhost} isAdmin={isAdmin} onGrade={onGrade} />
          </Fragment>
        );
      })}
    </div>
  );
}
